<template>
  <div class="p-10 flex flex-col flex-grow">
    <div class="w-full rounded-lg shadow-sm bg-white p-3 flex flex-col">
      <h1 class="text-md">{{ translate('QR Ordering') }}</h1>
      <form id="qrordering.form" @submit.prevent="generateQrCode" class="flex flex-col">
        <!-- Main container: two columns via flex -->
        <div class="flex flex-col md:flex-row gap-4 mt-4">
          <!-- Left form column -->
          <div class="flex flex-col gap-4 flex-1">
            
            <div class="grid grid-cols-2 gap-4">
              <!-- QR Type -->
              <div>
                <label>{{ translate('QR Type') }}</label>
                <BaseField
                  id="qrordering.form.select.qrtype"
                  type="select"
                  :options="qrTypeOptions"
                  :error="!!formErrors.qr_type"
                  :hint="formErrors.qr_type || ''"
                  v-model="form.qr_type"
                />
              </div>

              <!-- Branch (will vanish when qr_type==='deliver') -->
              <div v-if="form.qr_type !== 'deliver'">
                <label>{{ translate('Branch') }}</label>
                <BaseField
                  id="qrordering.form.select.branch"
                  type="select"
                  :options="branchOptions"
                  :error="!!formErrors.branch"
                  :hint="formErrors.branch || ''"
                  v-model="form.branch"
                />
              </div>

              <!-- Application -->
              <div>
                <label>{{ translate('Application') }}</label>
                <BaseField
                  id="qrordering.form.select.application"
                  type="select"
                  :options="applicationOptions"
                  :error="!!formErrors.application"
                  :hint="formErrors.application || ''"
                  v-model="form.application"
                />
              </div>

              <!-- Coupon -->
              <div>
                <label>{{ translate('Coupon') }}</label>
                <BaseField
                  id="qrordering.form.select.coupon"
                  type="select"
                  :options="couponOptions"
                  v-model="form.coupon"
                />
              </div>
            </div>

            <!-- Row 3: QR Code Options -->
            <div class="flex flex-col">
              <div class="w-1/2" v-if="form.qr_type !== 'deliver'">
                <label class="flex items-center gap-3">
                  <input v-model="form.printBranch" type="checkbox" />
                  {{ translate('Print branch name underneath the QR code') }}
                </label>
              </div>
              <div class="w-1/2" v-if="form.qr_type === 'eat-in'">
                <label class="flex items-center gap-3">
                  <input v-model="form.printTable" type="checkbox" />
                  {{ translate('Print table label underneath the QR code') }}
                </label>
              </div>
            </div>

            <!-- TABLE SETUP -->
            <div v-if="form.qr_type === 'eat-in'">
              <label>{{ translate('Tables') }}</label>
              <div class="flex gap-4 mb-3">
                <span class="flex items-center gap-3">
                  <input value="sequence" type="radio" v-model="form.table_type" />
                  {{ translate('Sequence') }}
                </span>
                <span class="flex items-center gap-3">
                  <input value="free-text" type="radio" v-model="form.table_type" />
                  {{ translate('Free Text') }}
                </span>
              </div>

              <div class="w-full border p-3 rounded">
                <!-- Sequence -->
                <template v-if="form.table_type === 'sequence'">
                  <div class="flex gap-4">
                    <div class="w-1/2">
                      <label>{{ translate('From') }}</label>
                      <BaseField
                        id="qrordering.form.input.from"
                        type="input"
                        v-model="tables.from"
                        inputType="number"
                        min="1"
                        :max="tables.to"
                      />
                    </div>
                    <div class="w-1/2">
                      <label>{{ translate('To') }}</label>
                      <BaseField
                        id="qrordering.form.input.to"
                        type="input"
                        v-model="tables.to"
                        inputType="number"
                        min="1"
                      />
                    </div>
                  </div>
                </template>

                <!-- Free Text -->
                <template v-if="form.table_type === 'free-text'">
                  <div class="flex flex-col gap-3">
                    <div
                      class="flex items-center justify-between gap-4"
                      v-for="(table, key) in tables.custom"
                      :key="key"
                    >
                      <BaseField
                        id="qrordering.form.input.custom"
                        type="input"
                        v-model="table.value"
                      />
                      <i @click="removeTable(key)" class="fa fa-trash cursor-pointer"></i>
                    </div>

                    <BaseButton @click="addTable" type="button" class="flex-none" size="md">
                      <i class="fa fa-plus mr-2"></i> {{ translate('Add Table') }}
                    </BaseButton>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- QR Code Container -->
          <div
            v-if="form.qr_type"
            class="flex flex-wrap gap-4 flex-1 items-start"
          >
            <QueueBusterQRCode
              v-for="(table, key) in qrTables"
              :key="key"
              :qrSettings="{ ...qrSettings, tableName: table.value }"
            />
          </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-end mt-10">
          <BaseButton :loading="loading" id="qrordering.button.submit" variant="primary">
            {{ translate('Export to PDF') }}
          </BaseButton>
        </div>
      </form>
    </div>
  </div>
</template>
<script>
import { translations, notificationAlerts } from '@/mixins';
import BaseField from '@/components/V2/BaseField.vue';
import { Location, Concept, Applications, Coupon, QueueBuster as QueueBusterApi } from "@/services/SOLO";
import BaseButton from '@/components/V2/BaseButton.vue';
import QueueBusterQRCode from './components/V2/QueueBusterQRCode.vue';

export default {
  name: 'QR Ordering New',
  mixins: [translations, notificationAlerts],
  components: {
    BaseField,
    BaseButton,
    QueueBusterQRCode
  },
  data: () => {
    return {
      loading: false,
      form: {
        application: '',
        branch: '',
        qr_type: 'eat-in',
        printTable: false,
        printBranch: false,
        coupon: '',
        table_type: 'sequence',
        count: 1
      },
      formErrors: {},
      qrTypeOptions: [
        { label: 'Eat-in', value: 'eat-in' },
        { label: 'To-go', value: 'to-go' },
        { label: 'Delivery', value: 'deliver' },
      ],
      branches: [],
      applications: [],
      coupons: [],
      tables: {
        from: 1,
        to: 1,
        custom: [
          { value: 1 }
        ]
      }
    }
  },
  watch: {
    'form.table_type'(value){

      if(value == 'free-text') {
        this.setCustomTables();
      }

      if(value == 'sequence') {
        this.tables.from = 1;
        this.tables.to = 1;
      }
    },
    'tables.from'() {
      this.setCustomTables();
    },
    'tables.to'() {
      this.setCustomTables();
    },
    'form.qr_type'(value) {
      this.form.printTable = false;
      this.form.printBranch = false;
      this.resetTables();
    }
  },
  computed: {
    branchOptions() {
      return this.branches.map(item => {
        return {
          value: item.id,
          label: item.attributes.name
        }
      });
    },
    selectedBranch() {
      return this.branches.find(item => item.id === this.form.branch);
    },
    applicationOptions() {
      return this.applications.filter((item) => item.attributes['application-type'] === 'social-ordering').map(item => {
        return {
          value: item.id,
          label: item.attributes.label
        }
      });
    },
    selectedApplication() {
      return this.applications.find(item => item.id === this.form.application);
    },
    couponOptions() {
      return this.coupons.map(item => {
        return {
          value: item.id,
          label: item.attributes.description
        }
      });
    },
    selectedCoupon() {
      return this.coupons.find(item => item.id === this.form.coupon);
    },
    qrSettings() {
      return {
        baseUrl: this.selectedApplication.attributes['custom-domain'] ?? this.selectedApplication.attributes.hosts,
        branchName: this.selectedBranch.attributes.name,
        branchId: this.selectedBranch.id,
        coupon: this.selectedCoupon?.attributes.code,
        orderType: this.form.qr_type,
        showBranchName: this.form.printBranch,
        showTableName: this.form.printTable,
      }
    },
    qrTables() {
      return this.tables.custom.filter(item => item.value);
    },
    mappedQrTables() {
      return this.qrTables.map(item => {
        return item.value
      });
    }
  },
  methods: {
    async getApplications() {
      const response = await Applications.getAllApplications()
      this.applications = response.data.data
    },
    async getBranches() {
      const response = await Location.getAllLocations(1)
      this.branches = response.data.data
    },
    async getCoupons() {
      const response = await Coupon.all(1, true)
      this.coupons = response.data.data
    },
    async validateForm() {
      let newErrors = {};

      if (this.form.qr_type.trim() === '') {
        newErrors.qr_type = this.translate('Type is required');
      }

      if (this.form.branch.trim() === '') {
        newErrors.branch = this.translate('Branch is required');
      }
      
      if (this.form.application.trim() === '') {
        newErrors.application = this.translate('Application is required');
      }

      this.formErrors = newErrors;

      return Object.keys(newErrors).length === 0;
    },
    async generateQrCode() {
      const isValid = await this.validateForm();

      console.log(isValid);

      if(!isValid) {
        return false;
      }

      QueueBusterApi.download(
        this.form.qr_type, 
        this.form.count,
        this.form.branch, 
        this.selectedBranch.attributes.name, 
        this.tables.from, 
        this.tables.to, 
        false, 
        this.form.printBranch, 
        this.form.printTable, 
        this.mappedQrTables, 
        this.form.application
      ).then(response => {
        window.open(response.data.data.attributes['csv-uri'], '_blank');
      });
    },
    setCustomTables() {
      this.tables.custom = [];
      for (let i = parseInt(this.tables.from); i <= parseInt(this.tables.to); i++) {
        this.tables.custom.push({ value: i });
      }
    },
    addTable() {
      this.tables.custom.push({ value: null });
    },
    removeTable(key) {
      if(this.tables.custom.length === 1) {
        return false;
      }

      this.$delete(this.tables.custom, key);
    },
    async setInitialValue() {
      this.form.branch = this.branches[0]?.id;
      this.form.application = this.applications[0]?.id;
    },
    resetTables() {
      this.tables.from = 1;
      this.tables.to = 1;
      this.tables.custom = [{ value: 1 }];
    }
  },
  async mounted() {
    await Promise.all([
      this.getBranches(),
      this.getApplications(),
      this.getCoupons(),
    ]);

    this.setInitialValue()
  }
}
</script>